import type { ComponentPropsWithRef } from "react";
import { cx } from "../../cx";

export interface SwitchProps extends Omit<
  ComponentPropsWithRef<"button">,
  "onChange" | "role" | "aria-label" | "children"
> {
  readonly checked: boolean;
  readonly onCheckedChange: (checked: boolean) => void;
  /** Accessible name (required: the visible label is usually elsewhere). */
  readonly label: string;
}

/** `role=switch` toggle: 44 px target on touch, visual 40×22 track. */
export function Switch({
  checked,
  onCheckedChange,
  label,
  className,
  disabled,
  ...rest
}: SwitchProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={() => onCheckedChange(!checked)}
      className={cx(
        "relative inline-flex h-5.5 w-10 shrink-0 items-center rounded-full transition-colors",
        "before:absolute before:-inset-x-1 before:-inset-y-3 before:content-['']",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus disabled:cursor-not-allowed disabled:opacity-60",
        checked ? "bg-action-primary" : "bg-line-strong",
        className,
      )}
      {...rest}
    >
      <span
        aria-hidden="true"
        className={cx(
          "inline-block size-4.5 rounded-full bg-surface shadow transition-transform motion-reduce:transition-none",
          checked ? "translate-x-4.5 rtl:-translate-x-4.5" : "translate-x-0.5 rtl:-translate-x-0.5",
        )}
      />
    </button>
  );
}
